/**
 * Common math, color, and 3D geometry utility functions.
 * Used for interpolation, color conversions, easing, and quaternion/vector math.
 */
// ============== Quaternion Utilities ==============
/**
 * Create an identity quaternion (no rotation).
 */
export function identityQuat() {
    return { x: 0, y: 0, z: 0, w: 1 };
}
/**
 * Get the inverse (conjugate) of a quaternion.
 * For unit quaternions, conjugate = inverse.
 */
export function invertQuat(q) {
    return { x: -q.x, y: -q.y, z: -q.z, w: q.w };
}
/**
 * Multiply two quaternions: result = a * b
 * Applies rotation b first, then a (standard quaternion composition).
 */
export function multiplyQuats(a, b) {
    return {
        x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
        y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
        z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
        w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    };
}
/**
 * Apply quaternion rotation to a vector.
 * Returns a new vector (does not mutate input).
 */
export function applyQuatToVec3(v, q) {
    const qx = q.x, qy = q.y, qz = q.z, qw = q.w;
    const vx = v.x, vy = v.y, vz = v.z;
    // t = 2 * cross(q.xyz, v)
    const tx = 2 * (qy * vz - qz * vy);
    const ty = 2 * (qz * vx - qx * vz);
    const tz = 2 * (qx * vy - qy * vx);
    // result = v + w * t + cross(q.xyz, t)
    return {
        x: vx + qw * tx + (qy * tz - qz * ty),
        y: vy + qw * ty + (qz * tx - qx * tz),
        z: vz + qw * tz + (qx * ty - qy * tx),
    };
}
/**
 * `applyQuatToVec3` into a caller-owned scratch, from scalars — the per-voxel form. Same arithmetic.
 */
export function applyQuatToVec3Into(out, vx, vy, vz, q) {
    const qx = q.x, qy = q.y, qz = q.z, qw = q.w;
    const tx = 2 * (qy * vz - qz * vy);
    const ty = 2 * (qz * vx - qx * vz);
    const tz = 2 * (qx * vy - qy * vx);
    out.x = vx + qw * tx + (qy * tz - qz * ty);
    out.y = vy + qw * ty + (qz * tx - qx * tz);
    out.z = vz + qw * tz + (qx * ty - qy * tx);
}
/**
 * Create a quaternion for rotation around the X axis.
 * @param radians - The rotation angle in radians
 */
export function xRotationQuat(radians) {
    const halfAngle = radians * 0.5;
    return {
        x: Math.sin(halfAngle),
        y: 0,
        z: 0,
        w: Math.cos(halfAngle),
    };
}
/**
 * Create a quaternion for rotation around the Y axis.
 * @param radians - The rotation angle in radians
 */
export function yRotationQuat(radians) {
    const halfAngle = radians * 0.5;
    return {
        x: 0,
        y: Math.sin(halfAngle),
        z: 0,
        w: Math.cos(halfAngle),
    };
}
/**
 * Create a quaternion for rotation around the Z axis.
 * @param radians - The rotation angle in radians
 */
export function zRotationQuat(radians) {
    const halfAngle = radians * 0.5;
    return {
        x: 0,
        y: 0,
        z: Math.sin(halfAngle),
        w: Math.cos(halfAngle),
    };
}
/**
 * Compose a quaternion from Euler angles (XYZ order).
 * @param xRad - Rotation around X axis in radians
 * @param yRad - Rotation around Y axis in radians
 * @param zRad - Rotation around Z axis in radians
 */
export function eulerToQuat(xRad, yRad, zRad) {
    return multiplyQuats(multiplyQuats(zRotationQuat(zRad), yRotationQuat(yRad)), xRotationQuat(xRad));
}
/**
 * Extract Euler angles (XYZ order) from a quaternion.
 * Returns angles in radians.
 */
export function quatToEuler(q) {
    // Roll (X)
    const sinrCosp = 2 * (q.w * q.x + q.y * q.z);
    const cosrCosp = 1 - 2 * (q.x * q.x + q.y * q.y);
    const x = Math.atan2(sinrCosp, cosrCosp);
    // Pitch (Y)
    const sinp = 2 * (q.w * q.y - q.z * q.x);
    const y = Math.abs(sinp) >= 1 ? (Math.sign(sinp) * Math.PI) / 2 : Math.asin(sinp);
    // Yaw (Z)
    const sinyCosp = 2 * (q.w * q.z + q.x * q.y);
    const cosyCosp = 1 - 2 * (q.y * q.y + q.z * q.z);
    const z = Math.atan2(sinyCosp, cosyCosp);
    return { x, y, z };
}
// ============== Interpolation ==============
/**
 * Linear interpolation between two numbers.
 */
/** A full turn. Spelled out often enough (window angles, spiral stairs, crenellations, arc sweeps). */
export const TWO_PI = Math.PI * 2;
/**
 * Rotate (x, z) by an angle whose cos/sin the caller has already computed, INTO a scratch object.
 *
 * Every user of this is a per-voxel or per-lattice-point loop — a building's ~95k SDF samples, the
 * parts rasterizer's per-part inner loop — so returning a fresh `{rx, rz}` allocates once per sample.
 * The scratch form is why there is one definition of the two lines rather than a copy inlined at each
 * hot site: pass the sign you want in `sin` (negate it to rotate the other way).
 */
export function rotateXZInto(out, x, z, cos, sin) {
    const rx = x * cos - z * sin;
    out.z = x * sin + z * cos;
    out.x = rx;
}
export function lerp(a, b, t) {
    return a + (b - a) * t;
}
/**
 * Smooth step function for nicer transitions.
 * Returns a value that smoothly interpolates from 0 to 1 as x moves from edge0 to edge1.
 */
export function smoothstep(edge0, edge1, x) {
    const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
}
/**
 * d(smoothstep)/dx — needed wherever a smoothstep FADES A QUANTITY THAT IS ITSELF A FUNCTION OF x,
 * because then the fade's own slope is part of the result's slope. Zero outside [edge0, edge1].
 */
export function smoothstepSlope(edge0, edge1, x) {
    const w = edge1 - edge0;
    if (w === 0)
        return 0;
    const t = (x - edge0) / w;
    if (t <= 0 || t >= 1)
        return 0;
    return (6 * t * (1 - t)) / w;
}
/**
 * Clamp a value between min and max.
 */
export function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
/**
 * Parse hex color string to RGB components (0-1 range).
 */
export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result)
        return { r: 1, g: 1, b: 1 };
    return {
        r: parseInt(result[1], 16) / 255,
        g: parseInt(result[2], 16) / 255,
        b: parseInt(result[3], 16) / 255,
    };
}
/**
 * Convert RGB components (0-1 range) to hex color string.
 */
export function rgbToHex(r, g, b) {
    const toHex = (c) => Math.round(Math.max(0, Math.min(1, c)) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
/**
 * Linearly interpolate between two hex color strings.
 */
export function lerpColor(colorA, colorB, t) {
    const a = hexToRgb(colorA);
    const b = hexToRgb(colorB);
    return rgbToHex(a.r + (b.r - a.r) * t, a.g + (b.g - a.g) * t, a.b + (b.b - a.b) * t);
}
/**
 * Convert RGB (0-1) to HSV (h 0-360, s/v 0-1).
 */
export function rgbToHsv(r, g, b) {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === r)
            h = ((g - b) / d) % 6;
        else if (max === g)
            h = (b - r) / d + 2;
        else
            h = (r - g) / d + 4;
        h *= 60;
        if (h < 0)
            h += 360;
    }
    const s = max === 0 ? 0 : d / max;
    return { h, s, v: max };
}
/**
 * Convert HSV (h 0-360, s/v 0-1) to RGB (0-1).
 */
export function hsvToRgb(h, s, v) {
    h = ((h % 360) + 360) % 360;
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0, g = 0, b = 0;
    if (h < 60) {
        r = c;
        g = x;
    }
    else if (h < 120) {
        r = x;
        g = c;
    }
    else if (h < 180) {
        g = c;
        b = x;
    }
    else if (h < 240) {
        g = x;
        b = c;
    }
    else if (h < 300) {
        r = x;
        b = c;
    }
    else {
        r = c;
        b = x;
    }
    return { r: r + m, g: g + m, b: b + m };
}
//# sourceMappingURL=math.js.map